import React from 'react';
import { Box, Typography } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { themeColors } from '../../styles';

export type NotificationsEmptyVariant = 'empty' | 'noPermission';

interface Props {
  variant?: NotificationsEmptyVariant;
}

const COPY: Record<NotificationsEmptyVariant, { title: string; subtitle: string }> = {
  // Has permission, simply nothing yet.
  empty: {
    title: 'אין התראות חדשות',
    subtitle: 'כאן יופיעו עדכונים על ההזמנות וקריאות השירות שלך ברגע שיהיו.',
  },
  // Lacks orders/service view permission — replaces the old bare line that was
  // pushed 200px down and read as a blank page.
  noPermission: {
    title: 'אין הרשאה לצפייה בהתראות',
    subtitle: 'לחשבון זה אין הרשאה לצפות בהזמנות ובקריאות שירות. לפרטים נוספים יש לפנות למנהל המערכת.',
  },
};

/**
 * Notifications-page empty state, so the page never reads as a broken blank
 * screen. Two variants: nothing to show yet, and no permission to view.
 */
const NotificationsEmpty = ({ variant = 'empty' }: Props) => {
  const { title, subtitle } = COPY[variant];
  const icon =
    variant === 'noPermission' ? (
      <LockOutlinedIcon sx={{ fontSize: 46, color: themeColors.placeholdersInput }} />
    ) : (
      <NotificationsNoneOutlinedIcon sx={{ fontSize: 48, color: themeColors.primary }} />
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 7,
      }}
    >
      <Box
        sx={{
          width: 104,
          height: 104,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: variant === 'noPermission' ? themeColors.baseDark : themeColors.newNotificationBase,
          mb: 2.5,
        }}
      >
        {icon}
      </Box>

      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: themeColors.title, mb: 1 }}>
        {title}
      </Typography>

      <Typography
        sx={{ fontSize: '14px', lineHeight: '21px', color: themeColors.paragraphInputTag, maxWidth: 300 }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
};

export default NotificationsEmpty;
